import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import PlatformCards from '../components/PlatformCards';
import UrlInputSection from '../components/UrlInputSection';
import HowItWorks from '../components/HowItWorks';
import InfoSection from '../components/InfoSection';
import SEOContent from '../components/SEOContent';
import FAQ from '../components/FAQ';
import Footer from '../components/Footer';
import VideoInfo from '../components/VideoInfo';
import DownloadButtons from '../components/DownloadButtons';
import Toast from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import MaintenancePage from './MaintenancePage';
import useWebsiteTheme from '../hooks/useWebsiteTheme';
import '../website-theme.css';
import {
  fetchYouTubeVideoInfo,
  fetchInstagramVideoInfo,
  fetchFacebookVideoInfo,
  fetchTikTokVideoInfo,
  validateYouTubeUrl,
  validateInstagramUrl,
  validateFacebookUrl,
  validateTikTokUrl,
} from '../utils/api';

const Home = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('youtube');
  const [videoData, setVideoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  
  // Initialize website theme
  const { currentTheme } = useWebsiteTheme();

  // Check maintenance mode on mount and listen for changes
  useEffect(() => {
    const checkMaintenanceMode = () => {
      const maintenanceMode = localStorage.getItem('maintenance_mode') === 'true';
      setIsMaintenanceMode(maintenanceMode);
    };

    // Check on mount
    checkMaintenanceMode();

    // Listen for storage changes (when changed in another tab or admin panel)
    window.addEventListener('storage', checkMaintenanceMode);

    // Also check periodically in case it's changed in the same tab
    const interval = setInterval(checkMaintenanceMode, 1000);

    return () => {
      window.removeEventListener('storage', checkMaintenanceMode);
      clearInterval(interval);
    };
  }, []);

  // If maintenance mode is enabled, show maintenance page
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  const handlePlatformSelect = (platformId) => {
    setSelectedPlatform(platformId);
    setVideoData(null);
  };

  const handleUrlSubmit = async (url, setError) => {
    setIsLoading(true);
    setVideoData(null);

    try {
      let data;

      if (selectedPlatform === 'youtube') {
        if (!validateYouTubeUrl(url)) {
          setError('Please enter a valid YouTube URL');
          setIsLoading(false);
          return;
        }
        data = await fetchYouTubeVideoInfo(url);
      } else if (selectedPlatform === 'instagram') {
        if (!validateInstagramUrl(url)) {
          setError('Please enter a valid Instagram URL (Reel, Post, or TV)');
          setIsLoading(false);
          return;
        }
        data = await fetchInstagramVideoInfo(url);
      } else if (selectedPlatform === 'facebook') {
        if (!validateFacebookUrl(url)) {
          setError('Please enter a valid Facebook video URL');
          setIsLoading(false);
          return;
        }
        data = await fetchFacebookVideoInfo(url);
      } else if (selectedPlatform === 'tiktok') {
        if (!validateTikTokUrl(url)) {
          setError('Please enter a valid TikTok video URL');
          setIsLoading(false);
          return;
        }
        data = await fetchTikTokVideoInfo(url);
      } else {
        setToastMessage('This platform is coming soon!');
        setTimeout(() => setToastMessage(''), 3000);
        setIsLoading(false);
        return;
      }

      setVideoData(data);
    } catch (error) {
      setToastMessage(error.message);
      setTimeout(() => setToastMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="website-theme min-h-screen" style={{ background: `linear-gradient(to bottom, ${currentTheme?.background || '#0a0a1f'}, ${currentTheme?.accent || '#1a1a3e'}, ${currentTheme?.background || '#0a0a1f'})` }}>
      <Toast message={toastMessage} onClose={() => setToastMessage('')} />
      <Navbar 
        selectedPlatform={selectedPlatform}
        onSelectPlatform={handlePlatformSelect}
      />
      <HeroSection />
      <div id="platform-section">
        <PlatformCards 
          selectedPlatform={selectedPlatform}
          onSelectPlatform={handlePlatformSelect}
        />
      </div>
      <UrlInputSection 
        selectedPlatform={selectedPlatform}
        onSubmit={handleUrlSubmit}
        isLoading={isLoading}
      />

      {isLoading && <LoadingSpinner />}

      {!isLoading && videoData && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <VideoInfo videoData={videoData} />
          <DownloadButtons links={videoData.links} />
        </div>
      )}

      <HowItWorks />
      <SEOContent />
      <div id="faq-section">
        <FAQ />
      </div>
      <Footer />
    </div>
  );
};

export default Home;
